import { Field, useFormikContext } from 'formik';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { config, getAssetUrl } from '../../../utils/config';
import { Country } from '../../../utils/countryData';
import Button from '../../atoms/Button';
import SubHeadline from '../../atoms/SubHeadline';
import AddressField from '../../fields/AddressField';
import AddressFieldNl from '../../fields/AddressFieldNl';
import FirstNameField from '../../fields/FirstNameField';
import LastNameField from '../../fields/LastNameField';
import SelectField from '../../formik/SelectField';
import AddressBook from '../AddressBook';
import DynamicFieldArea from '../DynamicFieldArea';
import './style.scss';

export type AddressFormProps = {
  headline?: ReactNode;
  context: 'shippingAddress' | 'billingAddress';
  availableCountries: Country[];
};

const specialComponents = {
  NL: AddressFieldNl,
};

const AddressForm = (props: AddressFormProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { formatMessage } = useIntl();
  const [isAddressBookOpen, setAddressBookOpen] = useState(false);

  const { headline, context, availableCountries } = props;
  const selectedCountry: Country = values[context].country;
  const { title: titleConfig, specialComponent } = config.addressForm[selectedCountry];

  const AddressFormComponent =
    specialComponent && selectedCountry in specialComponents ? specialComponents[selectedCountry] : AddressField;

  const onCountryChange = () => {
    setFieldValue(`${context}.street`, '');
    setFieldValue(`${context}.houseNumber`, '');
    setFieldValue(`${context}.streetHouseNumber`, '');
    setFieldValue(`${context}.additional`, '');
    setFieldValue(`${context}.zipCode`, '');
    setFieldValue(`${context}.city`, '');
    setFieldValue(`${context}.state`, '');
  };

  return (
    <div>
      {headline && (
        <div className="address-form__headline">
          <SubHeadline>{headline}</SubHeadline>
        </div>
      )}
      {isAddressBookOpen && <AddressBook context={context} onClose={() => setAddressBookOpen(false)} />}
      <div className="form">
        {config.features.addressBook.enabled && (
          <Button className="address-form__address-book-btn" type="button" onClick={() => setAddressBookOpen(true)}>
            <FormattedMessage id="address.addressBook" />
          </Button>
        )}
        <div className="form__row">
          <div className="form__field-container address-form__top-dropdown">
            <Field
              name={`${context}.gender`}
              component={SelectField}
              label={formatMessage({ id: 'generic.salutation.label' })}
              options={['f', 'm'].map(value => ({
                label: formatMessage({ id: `generic.salutation.options.${value}` }),
                value,
              }))}
              data-test-id={`${context}-gender`}
            />
          </div>
          {titleConfig?.enabled && (
            <div className="form__field-container address-form__top-dropdown">
              <Field
                name={`${context}.title`}
                component={SelectField}
                label={formatMessage({ id: 'address.title.label' })}
                options={['phD', 'prof'].map(value => ({
                  label: formatMessage({ id: `address.title.options.${value}` }),
                  value,
                }))}
                isClearable
                data-test-id={`${context}-title`}
              />
            </div>
          )}
          <div className="form__field-container address-form__top-dropdown">
            <Field
              name={`${context}.country`}
              component={SelectField}
              label={formatMessage({ id: 'address.country' })}
              options={availableCountries?.map(value => ({
                label: value,
                value,
                icon: getAssetUrl(`flags/${value.toLowerCase()}.svg`),
              }))}
              onChange={onCountryChange}
              data-test-id={`${context}-country`}
            />
          </div>
        </div>
        <div className="form__row address-form__row--names">
          <div className="form__field-container">
            <FirstNameField testIdPrefix={context} context={context} />
          </div>
          <div className="form__field-container">
            <LastNameField testIdPrefix={context} context={context} />
          </div>
        </div>

        <AddressFormComponent testIdPrefix={context} context={context} />

        <DynamicFieldArea
          area={context}
          context={context}
          conditionValues={{
            carrierGroupKey: values.carrier?.carrierGroupKey,
            shippingOptionKey: values.carrier?.shippingOptionKey,
            shippingCountry: context === 'shippingAddress' ? selectedCountry : undefined,
            billingCountry: context === 'billingAddress' ? selectedCountry : undefined,
          }}
        />
      </div>
    </div>
  );
};

export default AddressForm;
